import { zodResolver } from '@hookform/resolvers/zod'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettingsOutlined'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Alert, Box, Button, IconButton, InputAdornment, Link, Paper, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, Link as RouterLink, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { ApiError, api, setCsrf } from '../api/client'
import { keys, useMe } from '../api/hooks'
import type { User } from '../api/types'
import { Wordmark } from '../lib/Wordmark'

const schema = z.object({
  user: z.string().trim().min(1, 'Enter your username'),
  password: z.string().min(1, 'Enter your password'),
  totp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code').optional().or(z.literal('')),
})
type Form = z.infer<typeof schema>

/** Sign-in for administrators only. Same session as the mail login; a non-admin account is signed straight back out. */
export function AdminLoginPage() {
  const me = useMe()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [needTotp, setNeedTotp] = useState(false)
  const [show, setShow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Form>({ resolver: zodResolver(schema) })

  if (me.data?.role === 'ADMIN') return <Navigate to="/admin" replace />

  const signOutAndExplain = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined)
    qc.clear()
    setError('This account is not an administrator. Use the regular sign-in for mail.')
  }

  const onSubmit = handleSubmit(async (v) => {
    setError(null)
    try {
      // A plain username is the console-only admin; an address is a mailbox that happens to be an admin.
      const r = v.user.includes('@')
        ? await api<{ user: User; csrf: string }>('/auth/login', { method: 'POST', body: { email: v.user.trim(), password: v.password, totp: v.totp || undefined } })
        : await api<{ user: User; csrf: string }>('/auth/admin-login', { method: 'POST', body: { username: v.user.trim(), password: v.password } })
      setCsrf(r.csrf)
      if (r.user.role !== 'ADMIN') { await signOutAndExplain(); return }
      qc.setQueryData(keys.me, r.user)
      navigate('/admin', { replace: true })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TOTP_REQUIRED') { setNeedTotp(true); return }
      if (e instanceof ApiError && e.status === 429) setError(`Too many attempts. Wait ${Math.max(1, Math.ceil((e.body?.retryAfter ?? 60) / 60))} minute(s), then try again.`)
      else if (e instanceof ApiError && e.status === 401) setError(e.code === 'TOTP_INVALID' ? 'That verification code is not valid.' : 'Incorrect username or password.')
      else if (e instanceof ApiError && e.status === 400) setError('Enter your username and password.')
      else setError('Could not reach the mail server. Try again in a moment.')
    }
  })

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper component="main" variant="outlined" sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Wordmark size={24} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}><AdminPanelSettings fontSize="small" /><Typography variant="body2" sx={{ fontWeight: 600 }}>Admin</Typography></Box>
        </Box>
        <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'grid', gap: 2.5 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.15 }}>Admin sign in</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>Manage customers, domains and mailboxes.</Typography>
          </Box>
          {me.data && <Alert severity="info">You&apos;re signed in as {me.data.email}, which isn&apos;t an administrator. Sign in below with an admin account.</Alert>}
          {error && <Alert severity="error" role="alert">{error}</Alert>}
          <TextField label="Username" autoComplete="username" autoFocus fullWidth error={!!errors.user} helperText={errors.user?.message} slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } }} {...register('user')} />
          <TextField label="Password" type={show ? 'text' : 'password'} autoComplete="current-password" fullWidth error={!!errors.password} helperText={errors.password?.message}
            slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((s) => !s)} edge="end" size="small">{show ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> } }} {...register('password')} />
          {needTotp && <TextField label="Verification code" autoComplete="one-time-code" autoFocus fullWidth error={!!errors.totp} helperText={errors.totp?.message ?? 'From your authenticator app.'} slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }} {...register('totp')} />}
          <Button type="submit" variant="contained" size="large" disabled={isSubmitting} disableElevation sx={{ height: 48, fontWeight: 600 }}>{isSubmitting ? 'Signing in…' : needTotp ? 'Verify' : 'Sign in'}</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>Looking for your mailbox? <Link component={RouterLink} to="/login" underline="hover">Regular sign in</Link></Typography>
      </Paper>
    </Box>
  )
}
